import * as core from 'cw-ui-core';
import onOidcRedirecting from 'cw-ui-core/lib/components/oidc/actions/OidcRedirectingAction.js';
import { getOidcRedirecting } from 'cw-ui-core/lib/components/module/ModuleReducer.js';

const isSignInCallbackPath = () =>
  window.location.pathname.replace(/\/$/, '').endsWith('/signin-oidc');

/** OAuth authorization-code or error response still in the URL (handshake in progress). */
const hasOidcCallbackQueryParams = () => {
  const params = new URLSearchParams(window.location.search);
  return params.has('code') || params.has('error');
};

const isActiveOidcHandshake = () =>
  isSignInCallbackPath() || hasOidcCallbackQueryParams();

/**
 * Clears oidcRedirecting only when the user abandoned sign-in (browser back /
 * bfcache), not during a normal /signin-oidc callback or cold start.
 * Safe to call repeatedly: dispatches only when redirecting && !user.
 */
export const clearStaleOidcRedirectingState = () => {
  if (isActiveOidcHandshake()) {
    return;
  }

  const state = core.Store().getState();
  const isRedirecting = getOidcRedirecting(state.module);
  const hasUser = core.getUser(state.oidc);

  if (isRedirecting && !hasUser) {
    core.Store().dispatch(onOidcRedirecting(false));
  }
};

/**
 * BFCache restore and history Back can both fire in the same turn; coalesce to one run.
 */
let staleRedirectRecoveryScheduled = false;

const scheduleStaleOidcRedirectRecovery = () => {
  if (staleRedirectRecoveryScheduled) {
    return;
  }

  staleRedirectRecoveryScheduled = true;
  queueMicrotask(() => {
    staleRedirectRecoveryScheduled = false;
    clearStaleOidcRedirectingState();
  });
};

let listenersRegistered = false;

const onOidcRedirectRecoveryPageShow = event => {
  if (!event.persisted) {
    return;
  }

  scheduleStaleOidcRedirectRecovery();
};

const onOidcRedirectRecoveryPopState = () => {
  scheduleStaleOidcRedirectRecovery();
};

/**
 * Stuck oidcRedirecting survives only when the landing document is restored with
 * Redux intact (typical: Back from STS via back-forward cache).
 *
 * pageshow (event.persisted): BFCache restore — not run on ordinary pageshow after
 * forward navigation (persisted === false).
 *
 * popstate: history Back/Forward on a live document without reload; covers cases
 * where popstate fires without a persisted pageshow. On BFCache Back both may fire;
 * scheduleStaleOidcRedirectRecovery coalesces so we still dispatch at most once.
 */
export const registerOidcRedirectRecoveryListeners = () => {
  if (listenersRegistered) {
    return;
  }

  listenersRegistered = true;
  window.addEventListener('pageshow', onOidcRedirectRecoveryPageShow);
  window.addEventListener('popstate', onOidcRedirectRecoveryPopState);
};

if (typeof module !== 'undefined' && module.hot) {
  module.hot.dispose(() => {
    window.removeEventListener('pageshow', onOidcRedirectRecoveryPageShow);
    window.removeEventListener('popstate', onOidcRedirectRecoveryPopState);
    listenersRegistered = false;
  });
}
